import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { articleService, tripService, uploadService, UPLOADS_BASE_URL } from '../../services/api';
import { FaEdit, FaTrash, FaEye, FaPlus } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { Editor } from '@tinymce/tinymce-react';

const ArticleManagement = () => {
  const [articles, setArticles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'dive-sites',
    status: 'draft',
    tags: '',
    relatedTrips: [],
    metaDescription: '',
  });
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const categoryLabels = {
    'dive-sites': 'Dive Sites',
    'trip-reports': 'Trip Reports',
    'gear-reviews': 'Gear Reviews',
    'techniques': 'Diving Techniques',
    'safety': 'Safety',
    'beginner-guides': 'Beginner Guides',
    'destinations': 'Destinations',
    'marine-life': 'Marine Life',
    'conservation': 'Conservation',
    'news': 'News'
  };

  useEffect(() => {
    loadArticles();
    loadTrips();
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const response = await articleService.getAllArticlesAdmin();
      setArticles(response.data.articles);
    } catch (error) {
      toast.error('Failed to load articles');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrips = async () => {
    try {
      const response = await tripService.getAllTrips();
      setTrips(response.data);
    } catch (error) {
      console.error('Failed to load trips:', error);
    }
  };

  const handleOpenModal = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt,
        content: article.content,
        category: article.category,
        status: article.status,
        tags: article.tags.join(', '),
        relatedTrips: article.relatedTrips?.map(t => t._id || t) || [],
        metaDescription: article.metaDescription || '',
      });
      if (article.featuredImage && article.featuredImage !== 'default-article.jpg') {
        setImagePreview(`${UPLOADS_BASE_URL}/uploads/${article.featuredImage}`);
      }
    } else {
      setEditingArticle(null);
      setFormData({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        category: 'dive-sites',
        status: 'draft',
        tags: '',
        relatedTrips: [],
        metaDescription: '',
      });
      setImagePreview(null);
    }
    setFeaturedImage(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingArticle(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'dive-sites',
      status: 'draft',
      tags: '',
      relatedTrips: [],
      metaDescription: '',
    });
    setFeaturedImage(null);
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug from title
    if (name === 'title' && !editingArticle) {
      const slug = value
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/--+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({ ...prev, content }));
  };

  const handleTripSelection = (e) => {
    const options = Array.from(e.target.selectedOptions);
    const values = options.map(option => option.value);
    setFormData(prev => ({ ...prev, relatedTrips: values }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFeaturedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // TinyMCE image upload handler
  const handleTinyMCEImageUpload = (blobInfo, progress) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Convert blob to file
        const file = new File([blobInfo.blob()], blobInfo.filename(), { 
          type: blobInfo.blob().type 
        });

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
          reject('Image must be less than 5MB');
          return;
        }

        // Upload using our existing service
        const response = await uploadService.uploadImage(file);
        const imageUrl = `${UPLOADS_BASE_URL}${response.data.url}`;
        
        resolve(imageUrl);
      } catch (error) {
        reject('Image upload failed: ' + (error.message || 'Unknown error'));
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append('title', formData.title);
    data.append('slug', formData.slug);
    data.append('excerpt', formData.excerpt);
    data.append('content', formData.content);
    data.append('category', formData.category);
    data.append('status', formData.status);
    data.append('tags', formData.tags);
    data.append('metaDescription', formData.metaDescription);
    
    formData.relatedTrips.forEach((tripId) => {
      data.append('relatedTrips', tripId);
    });

    if (featuredImage) {
      data.append('featuredImage', featuredImage);
    }

    try {
      if (editingArticle) {
        await articleService.updateArticle(editingArticle._id, data);
        toast.success('Article updated successfully');
      } else {
        await articleService.createArticle(data);
        toast.success('Article created successfully');
      }
      handleCloseModal();
      loadArticles();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save article');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await articleService.deleteArticle(id);
        toast.success('Article deleted successfully');
        loadArticles();
      } catch (error) {
        toast.error('Failed to delete article');
        console.error(error);
      }
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h4>Article Management</h4>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <FaPlus className="me-2" />
          New Article
        </Button>
      </Card.Header>
      <Card.Body>
        {articles.length === 0 ? (
          <Alert variant="info">
            No articles yet. Create your first article to get started!
          </Alert>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Views</th>
                <th>Published</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((article) => (
                <tr key={article._id}>
                  <td>
                    <strong>{article.title}</strong>
                    <br />
                    <small className="text-muted">{article.slug}</small>
                  </td>
                  <td>
                    <Badge bg="info">{categoryLabels[article.category]}</Badge>
                  </td>
                  <td>
                    <Badge bg={
                      article.status === 'published' ? 'success' :
                      article.status === 'draft' ? 'warning' : 'secondary'
                    }>
                      {article.status}
                    </Badge>
                  </td>
                  <td>
                    <FaEye className="me-1" />
                    {article.views}
                  </td>
                  <td>{article.publishedAt ? formatDate(article.publishedAt) : '-'}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleOpenModal(article)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(article._id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{editingArticle ? 'Edit Article' : 'Create New Article'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter article title"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Slug *</Form.Label>
              <Form.Control
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                required
                placeholder="article-slug-url"
              />
              <Form.Text className="text-muted">
                URL-friendly version of the title (auto-generated)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Excerpt *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                required
                maxLength={300}
                placeholder="Brief summary (max 300 characters)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content *</Form.Label>
              <Editor
                apiKey="ivco8379bhn7po2xkn6jm5d3k0lsc65r9axetcsb04ebnq6s"
                value={formData.content}
                onEditorChange={handleContentChange}
                init={{
                  height: 500,
                  menubar: true,
                  plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                  ],
                  toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | image media link | code | help',
                  content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px }',
                  branding: false,
                  promotion: false,
                  // Image upload configuration
                  images_upload_handler: handleTinyMCEImageUpload,
                  automatic_uploads: true,
                  file_picker_types: 'image',
                  image_caption: true,
                  image_advtab: true,
                  image_title: true,
                  // Custom image styles
                  image_class_list: [
                    { title: 'Responsive', value: 'img-fluid' },
                    { title: 'Rounded', value: 'rounded' }
                  ],
                  // Link configuration
                  link_default_target: '_blank',
                  link_assume_external_targets: true,
                  // Paste configuration
                  paste_as_text: false,
                  paste_data_images: true,
                  // Additional settings
                  convert_urls: false,
                  relative_urls: false,
                  remove_script_host: false,
                }}
              />
              <Form.Text className="text-muted">
                Use the toolbar to format your content. Click the image icon to upload and insert images directly.
              </Form.Text>
            </Form.Group>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Status *</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Tags</Form.Label>
              <Form.Control
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="diving, scuba, underwater, etc. (comma-separated)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Featured Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Related Trips (Optional)</Form.Label>
              <Form.Select
                multiple
                value={formData.relatedTrips}
                onChange={handleTripSelection}
                size="sm"
                style={{ height: '120px' }}
              >
                {trips.map((trip) => (
                  <option key={trip._id} value={trip._id}>
                    {trip.name} - {trip.location}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Hold Ctrl/Cmd to select multiple trips
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Meta Description (SEO)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                maxLength={160}
                placeholder="SEO description (max 160 characters)"
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingArticle ? 'Update Article' : 'Create Article'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default ArticleManagement;
